import { useEffect, useState } from "react";
import { Camera, KeyRound, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";
import api, { getError } from "@/api";

const emptyProfile = {
  first_name: "",
  last_name: "",
  phone: "",
  address: "",
  service_date: "",
};

export default function AccountPage() {
  const [account, setAccount] = useState(null);
  const [profile, setProfile] = useState(emptyProfile);
  const [passwords, setPasswords] = useState({ current_password: "", new_password: "", confirm: "" });
  const [photoBusy, setPhotoBusy] = useState(false);

  const updateShell = (user) => window.dispatchEvent(new CustomEvent("ecom-profile-updated", { detail: user }));

  useEffect(() => {
    api.get("/auth/profile")
      .then((response) => {
        setAccount(response.data);
        setProfile({ ...emptyProfile, ...(response.data.profile || {}) });
      })
      .catch((error) => toast.error(getError(error)));
  }, []);

  const saveProfile = async (event) => {
    event.preventDefault();
    try {
      const response = await api.put("/auth/profile", profile);
      setAccount(response.data);
      updateShell(response.data);
      toast.success("Profil enregistré");
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const uploadPhoto = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setPhotoBusy(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const response = await api.post("/auth/profile/photo", data);
      setAccount(response.data);
      updateShell(response.data);
      toast.success("Photo de profil enregistrée");
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setPhotoBusy(false);
      event.target.value = "";
    }
  };

  const deletePhoto = async () => {
    try {
      const response = await api.delete("/auth/profile/photo");
      setAccount(response.data);
      updateShell(response.data);
      toast.success("Photo de profil supprimée");
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const savePassword = async (event) => {
    event.preventDefault();
    if (passwords.new_password !== passwords.confirm) return toast.error("Les mots de passe ne correspondent pas");
    try {
      await api.post("/auth/change-password", passwords);
      toast.success("Mot de passe modifié. Reconnectez-vous.");
    } catch (error) {
      toast.error(getError(error));
    }
  };

  const serviceDateEditable = account?.role === "admin_principal" || account?.service_date_edit_enabled;

  return (
    <div className="page-enter" data-testid="profile-page">
      <div className="page-heading">
        <div>
          <p className="eyebrow">MON PROFIL</p>
          <h1>Mon compte</h1>
          <p>Gérez vos informations, votre photo et la sécurité de votre accès.</p>
        </div>
      </div>
      <div className="settings-grid">
        <section className="data-panel" data-testid="profile-details-panel">
          <div className="panel-heading"><div><p className="eyebrow">IDENTITÉ</p><h2>Profil</h2></div><UserRound size={20} /></div>
          <div className="profile-photo-row" data-testid="profile-photo-section">
            <div className="profile-photo-preview">
              {account?.picture ? <img alt="Photo de profil" data-testid="profile-photo-preview" src={account.picture} /> : <UserRound size={30} />}
            </div>
            <div>
              <strong>Photo de profil</strong>
              <span>JPG, PNG ou WEBP · 5 Mo maximum</span>
              <div className="profile-photo-actions">
                <label className="secondary-action" data-testid="upload-profile-photo-label">
                  <Camera size={16} /> {photoBusy ? "Envoi…" : "Ajouter ou modifier"}
                  <input accept="image/jpeg,image/png,image/webp" data-testid="profile-photo-input" disabled={photoBusy} onChange={uploadPhoto} type="file" />
                </label>
                {account?.picture && <button className="secondary-action" data-testid="delete-profile-photo-button" onClick={deletePhoto} type="button"><Trash2 size={16} /> Supprimer</button>}
              </div>
            </div>
          </div>
          <form className="stack-form" data-testid="profile-details-form" onSubmit={saveProfile}>
            <input data-testid="profile-first-name-input" onChange={(event) => setProfile({ ...profile, first_name: event.target.value })} placeholder="Prénom" value={profile.first_name} />
            <input data-testid="profile-last-name-input" onChange={(event) => setProfile({ ...profile, last_name: event.target.value })} placeholder="Nom" value={profile.last_name} />
            <input data-testid="profile-phone-input" onChange={(event) => setProfile({ ...profile, phone: event.target.value })} placeholder="Téléphone" value={profile.phone} />
            <input data-testid="profile-address-input" onChange={(event) => setProfile({ ...profile, address: event.target.value })} placeholder="Adresse" value={profile.address} />
            <label className="profile-service-date-field">
              <span>Date de début de service</span>
              <input data-testid="profile-service-date-input" disabled={!serviceDateEditable} lang="fr-FR" onChange={(event) => setProfile({ ...profile, service_date: event.target.value })} type="date" value={profile.service_date || ""} />
              <small data-testid="service-date-format-hint">Format attendu : jour / mois / année</small>
              {!serviceDateEditable && <small data-testid="service-date-locked-message">Modification désactivée par l’administrateur.</small>}
            </label>
            <button className="primary-action" data-testid="save-profile-button">Enregistrer mon profil</button>
          </form>
        </section>
        <section className="data-panel" data-testid="password-change-panel">
          <div className="panel-heading"><div><p className="eyebrow">SÉCURITÉ</p><h2>Mot de passe</h2></div><KeyRound size={20} /></div>
          <form className="stack-form" data-testid="password-change-form" onSubmit={savePassword}>
            <input data-testid="current-password-input" onChange={(event) => setPasswords({ ...passwords, current_password: event.target.value })} placeholder="Mot de passe actuel" required type="password" value={passwords.current_password} />
            <input data-testid="new-password-input" minLength="8" onChange={(event) => setPasswords({ ...passwords, new_password: event.target.value })} placeholder="Nouveau mot de passe" required type="password" value={passwords.new_password} />
            <input data-testid="confirm-new-password-input" minLength="8" onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} placeholder="Confirmer le nouveau mot de passe" required type="password" value={passwords.confirm} />
            <button className="primary-action" data-testid="change-password-button">Modifier le mot de passe</button>
          </form>
        </section>
      </div>
    </div>
  );
}